/**
 * Geräte-Status (Spülmaschine, Waschmaschine, Trockner): Datentypen und
 * reine Anzeige-Logik, gemeinsam für Server und Client.
 */

export interface ApplianceProgram {
  label: string;
  minutes: number;
}

export interface ApplianceDTO {
  id: number;
  name: string;
  icon: string;
  defaultMinutes: number;
  programs: ApplianceProgram[];
  sort: number;
  active: boolean;
  /** Steckdose angeschlossen (Messwerte kommen) */
  hasSensor: boolean;
  lastWatts: number | null;
  lastPowerAt: number | null;
}

/** Nur für Admins: Sensor-Konfiguration */
export interface ApplianceAdminDTO extends ApplianceDTO {
  sensorKey: string | null;
  sensorHost: string | null;
  startWatts: number;
  startSeconds: number;
  endWatts: number;
  endMinutes: number;
}

export type RunStatus = 'running' | 'finished' | 'emptied' | 'cancelled';
export type RunPhase = 'laeuft' | 'fertig' | 'erledigt' | 'abgebrochen';

export interface RunDTO {
  id: string;
  applianceId: number;
  source: 'manual' | 'sensor';
  startedBy: number | null;
  startedByName: string | null;
  startedAt: number;
  plannedMinutes: number;
  extendedMinutes: number;
  status: RunStatus;
  finishedAt: number | null;
  finishedSource: 'sensor' | null;
  sensorConfirmedAt: number | null;
  emptiedBy: number | null;
  emptiedByName: string | null;
  emptiedAt: number | null;
  cancelledBy: number | null;
  cancelledByName: string | null;
  cancelledAt: number | null;
  cancelReason: string;
  /** nur im Client: noch nicht hochgeladen */
  pending?: boolean;
}

/** Wenn die Steckdose das Ende meldet, wartet die Anzeige so lange über die geplante Zeit hinaus. */
export const SENSOR_GRACE_MINUTES = 60;

export function expectedEnd(r: Pick<RunDTO, 'startedAt' | 'plannedMinutes' | 'extendedMinutes'>): number {
  return r.startedAt + (r.plannedMinutes + r.extendedMinutes) * 60_000;
}

export function sensorTracked(r: Pick<RunDTO, 'source' | 'sensorConfirmedAt'>): boolean {
  return r.source === 'sensor' || r.sensorConfirmedAt !== null;
}

export function runPhase(r: RunDTO, now: number): RunPhase {
  if (r.status === 'cancelled') return 'abgebrochen';
  if (r.status === 'emptied') return 'erledigt';
  if (r.status === 'finished' || r.finishedAt !== null) return 'fertig';
  const end = expectedEnd(r);
  // Mit Steckdose entscheidet der Sensor – außer er meldet sich viel zu lange nicht.
  const limit = sensorTracked(r) ? end + SENSOR_GRACE_MINUTES * 60_000 : end;
  return now < limit ? 'laeuft' : 'fertig';
}

/** Zeitpunkt, ab dem das Gerät als fertig gilt. */
export function finishedAt(r: RunDTO): number {
  if (r.finishedAt !== null) return r.finishedAt;
  return sensorTracked(r) ? expectedEnd(r) + SENSOR_GRACE_MINUTES * 60_000 : expectedEnd(r);
}

/** Läuft oder wartet aufs Ausräumen → belegt das Gerät. */
export function isActive(r: RunDTO): boolean {
  return r.status === 'running' || r.status === 'finished';
}

/** Restzeit als 'H:MM' (bzw. '0:00'), ohne Sekunden. */
export function formatRemaining(ms: number): string {
  const m = Math.max(0, Math.ceil(ms / 60_000));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

/** 'Kurz=60, Normal=150' ↔ Programme */
export function parsePrograms(text: string): ApplianceProgram[] {
  return text
    .split(/[,;\n]/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const m = /^(.+?)\s*[=:]\s*(\d{1,3})$/.exec(p);
      if (!m) throw new Error(`Programm „${p}" bitte als Name=Minuten angeben.`);
      const minutes = Number(m[2]);
      if (minutes < 1 || minutes > 600) throw new Error(`Dauer für „${m[1]}" muss zwischen 1 und 600 Minuten liegen.`);
      return { label: m[1].slice(0, 30), minutes };
    });
}

export function formatPrograms(list: ApplianceProgram[]): string {
  return list.map((p) => `${p.label}=${p.minutes}`).join(', ');
}
